import React, { useState, useRef } from 'react';
import { submitBugReport } from '../api/apiClient';
import './BugReportButton.css';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const BugReportButton = () => {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const close = () => {
    setOpen(false);
    setDescription('');
    setImage(null);
    setDone(false);
    setError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please choose an image file.'); return; }
    if (file.size > MAX_IMAGE_BYTES) { setError('Image must be under 5MB.'); return; }
    setError('');
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await submitBugReport({
        description: description.trim(),
        page_url: window.location.pathname,
        user_agent: navigator.userAgent,
        image,
      });
      setDone(true);
      setTimeout(close, 1600);
    } catch {
      setError('Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="bug-fab"
        onClick={() => setOpen(true)}
        title="Report a bug"
        aria-label="Report a bug"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 8h-2.81c-.45-.78-1.07-1.45-1.82-1.96L17 4.41 15.59 3l-2.17 2.17C13 5.06 12.51 5 12 5s-1 .06-1.41.17L8.41 3 7 4.41l1.62 1.63C7.88 6.55 7.26 7.22 6.81 8H4v2h2.09c-.05.33-.09.66-.09 1v1H4v2h2v1c0 .34.04.67.09 1H4v2h2.81c1.04 1.79 2.97 3 5.19 3s4.15-1.21 5.19-3H20v-2h-2.09c.05-.33.09-.66.09-1v-1h2v-2h-2v-1c0-.34-.04-.67-.09-1H20V8zm-6 8h-4v-2h4v2zm0-4h-4v-2h4v2z"/>
        </svg>
      </button>

      {open && (
        <div className="bug-overlay" onClick={close}>
          <div className="bug-card" onClick={e => e.stopPropagation()}>
            {done ? (
              <div className="bug-done">
                <span className="bug-done-check">✓</span>
                <p>Thanks — we got it.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h3 className="bug-title">Report a bug</h3>
                <p className="bug-subtitle">Found something broken? Let us know.</p>
                <textarea
                  className="bug-textarea"
                  placeholder="What happened?"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  autoFocus
                  rows={4}
                />

                {image ? (
                  <div className="bug-image-preview">
                    <img src={image} alt="Attached screenshot" />
                    <button type="button" className="bug-image-remove" onClick={() => setImage(null)} aria-label="Remove image">×</button>
                  </div>
                ) : (
                  <button type="button" className="bug-attach-btn" onClick={() => fileInputRef.current?.click()}>
                    📎 Attach a screenshot
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />

                {error && <p className="bug-error">{error}</p>}
                <div className="bug-actions">
                  <button type="button" className="bug-cancel" onClick={close}>Cancel</button>
                  <button type="submit" className="bug-submit" disabled={submitting || !description.trim()}>
                    {submitting ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BugReportButton;
